/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import {
  hapticSelection,
  hapticSuccess,
  hapticTap,
} from "../utils/haptics";
import {
  getPushPermissionState,
  registerForPush,
  requestPushPermission,
} from "../services/notificationService";
import "../styles/parent/ParentOnboarding.css";

const STEPS = ["welcome", "child", "notifications", "biometric"];
const SESSION_WAIT_MS = 6500;

function OnboardingLoadingCard() {
  return (
    <div className="pp-onboarding-screen pp-onboarding-loading-screen">
      <div className="pp-onboarding-shell pp-onboarding-loading-shell">
        <div className="pp-onboarding-card pp-onboarding-loading-card">
          <div className="pp-onboarding-icon">🔐</div>
          <p className="pp-onboarding-eyebrow">PresentPlus parent access</p>
          <h1 className="pp-onboarding-title">Setting up your parent app</h1>
          <p className="pp-onboarding-copy">
            We are securely connecting your account to the child records approved by your school.
          </p>
        </div>
      </div>
    </div>
  );
}

function OnboardingSessionCard({ onSignIn }) {
  return (
    <div className="pp-onboarding-screen pp-onboarding-loading-screen">
      <div className="pp-onboarding-shell pp-onboarding-loading-shell">
        <div className="pp-onboarding-card pp-onboarding-loading-card">
          <div className="pp-onboarding-icon">🔐</div>
          <p className="pp-onboarding-eyebrow">Session check</p>
          <h1 className="pp-onboarding-title">Please sign in again</h1>
          <p className="pp-onboarding-copy">
            Your parent account was created, but this device does not have an active session yet.
          </p>

          <button
            type="button"
            className="pp-onboarding-btn primary"
            onClick={onSignIn}
          >
            Go to parent login
          </button>
        </div>
      </div>
    </div>
  );
}

function isParentRole(value) {
  return String(value || "").toLowerCase() === "parent";
}

function getLocalOnboardingDone(uid) {
  if (!uid) return false;
  try {
    return window.localStorage.getItem(`pp_parent_onboarding_done_${uid}`) === "true";
  } catch {
    return false;
  }
}

function setLocalOnboardingDone(uid) {
  if (!uid) return;
  try {
    window.localStorage.setItem(`pp_parent_onboarding_done_${uid}`, "true");
  } catch {}
}

export default function ParentOnboarding() {
  const navigate = useNavigate();
  const authContext = useAuth() || {};

  const {
    user: contextUser,
    profile,
    role,
    authReady,
    loading,
    profileLoading,
  } = authContext;

  const [authUser, setAuthUser] = useState(() => auth.currentUser || null);
  const [authResolved, setAuthResolved] = useState(Boolean(auth.currentUser));
  const [authWaitExpired, setAuthWaitExpired] = useState(false);

  const [stepIndex, setStepIndex] = useState(0);
  const [checking, setChecking] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabledState] = useState(false);
  const [onboardingNotice, setOnboardingNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [resolvedChildren, setResolvedChildren] = useState([]);
  const [fallbackProfile, setFallbackProfile] = useState(null);
  const [schoolProfile, setSchoolProfile] = useState(null);

  const user = contextUser || authUser;
  const contextIsReady = Boolean(authReady) || authResolved || authWaitExpired;

  const effectiveProfile = profile || fallbackProfile || {};
  const effectiveRole = role || effectiveProfile?.role || "";

  const currentStep = STEPS[stepIndex];
  const isNativeApp = Capacitor.isNativePlatform();

  const children = useMemo(
    () => (Array.isArray(effectiveProfile?.children) ? effectiveProfile.children : []),
    [effectiveProfile]
  );

  const schoolId = effectiveProfile?.schoolId || "";

  useEffect(() => {
    let mounted = true;

    async function loadSchoolProfile() {
      if (!schoolId) {
        if (mounted) setSchoolProfile(null);
        return;
      }

      try {
        const schoolSnap = await getDoc(doc(db, "schools", schoolId));

        if (!mounted) return;

        setSchoolProfile(
          schoolSnap.exists()
            ? { id: schoolSnap.id, ...(schoolSnap.data() || {}) }
            : null
        );
      } catch (err) {
        console.warn("Parent onboarding school lookup failed:", err);
        if (mounted) setSchoolProfile(null);
      }
    }

    loadSchoolProfile();

    return () => {
      mounted = false;
    };
  }, [schoolId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setAuthUser(nextUser || null);
      setAuthResolved(true);
    });

    const timer = window.setTimeout(() => {
      setAuthWaitExpired(true);
      setAuthResolved(true);
    }, SESSION_WAIT_MS);

    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadFallbackProfile() {
      if (!contextIsReady || !user || profile) return;

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (mounted && snap.exists()) {
          setFallbackProfile({ uid: user.uid, id: user.uid, ...snap.data() });
        }
      } catch (err) {
        console.error("Parent onboarding fallback profile error:", err);
      }
    }

    loadFallbackProfile();

    return () => {
      mounted = false;
    };
  }, [contextIsReady, user, profile]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!user && contextIsReady) {
        setChecking(false);
      }
    }, SESSION_WAIT_MS);

    return () => window.clearTimeout(timer);
  }, [user, contextIsReady]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      if (!contextIsReady || loading) return;

      if (!user) {
        if (!cancelled) setChecking(false);
        return;
      }

      if (profileLoading && !fallbackProfile && !profile && !authWaitExpired) {
        return;
      }

      const roleToCheck = effectiveRole;

      if (!isParentRole(roleToCheck)) {
        if (!cancelled) setChecking(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        const userData = snap.exists() ? snap.data() || {} : {};

        if (cancelled) return;

        if (
          userData.parentOnboardingCompleted === true ||
          userData.onboardingCompleted === true ||
          getLocalOnboardingDone(user.uid)
        ) {
          setCompleted(true);
        }

        setNotificationsEnabled(Boolean(userData.pushEnabled));

        const permission = await getPushPermissionState();
        if (!cancelled && permission?.receive === "granted") {
          setNotificationsEnabled(true);
        }

        /*
          Do not run biometric checks during onboarding boot.
          The parent session/profile may still be settling here, and biometric
          setup is handled securely from Profile after password confirmation.
        */
        if (!cancelled) {
          setBiometricAvailable(true);
          setBiometricEnabledState(Boolean(userData.biometricEnabled));
        }
      } catch (err) {
        console.error("Onboarding boot error:", err);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    boot();

    return () => {
      cancelled = true;
    };
  }, [
    contextIsReady,
    loading,
    profileLoading,
    user,
    effectiveRole,
    profile,
    fallbackProfile,
    authWaitExpired,
  ]);

  useEffect(() => {
    let mounted = true;

    async function resolveLinkedChildren() {
      if (!children.length) {
        if (mounted) setResolvedChildren([]);
        return;
      }

      const resolved = await Promise.all(
        children.map(async (item) => {
          if (typeof item === "object" && item) {
            return {
              id: item.id || item.studentId || "",
              name: item.name || item.fullName || "Child",
              year: item.year || item.className || item.yearGroup || "",
            };
          }

          if (typeof item === "string" && schoolId) {
            try {
              const studentRef = doc(db, "schools", schoolId, "students", item);
              const studentSnap = await getDoc(studentRef);

              if (studentSnap.exists()) {
                const student = studentSnap.data() || {};
                return {
                  id: item,
                  name:
                    student.fullName ||
                    student.name ||
                    student.displayName ||
                    student.firstName ||
                    "Child",
                  year:
                    student.year ||
                    student.className ||
                    student.yearGroup ||
                    "",
                };
              }
            } catch (err) {
              console.error("Failed to resolve onboarding child:", err);
            }
          }

          return {
            id: typeof item === "string" ? item : "",
            name: "Child",
            year: "",
          };
        })
      );

      if (mounted) setResolvedChildren(resolved);
    }

    resolveLinkedChildren();

    return () => {
      mounted = false;
    };
  }, [children, schoolId]);

  const resolvedSchoolName =
    effectiveProfile?.schoolName ||
    effectiveProfile?.schoolDisplayName ||
    schoolProfile?.schoolName ||
    schoolProfile?.name ||
    schoolProfile?.displayName ||
    schoolProfile?.schoolDisplayName ||
    "Your school";

  function goToParentLogin() {
    navigate("/parent-login", { replace: true });
  }

  if (!contextIsReady || loading || checking || (profileLoading && user && !fallbackProfile && !profile && !authWaitExpired)) {
    return <OnboardingLoadingCard />;
  }

  if (!user) {
    return <OnboardingSessionCard onSignIn={goToParentLogin} />;
  }

  if (!isParentRole(effectiveRole)) {
    return <Navigate to="/parent-login" replace />;
  }

  if (completed) {
    return <Navigate to="/parent/home" replace />;
  }

  async function nextStep() {
    await hapticTap();

    if (stepIndex === STEPS.length - 1) {
      await finishOnboarding();
      return;
    }

    setStepIndex((prev) => prev + 1);
  }

  async function prevStep() {
    await hapticSelection();
    setStepIndex((prev) => Math.max(0, prev - 1));
  }

  async function enableNotifications() {
    await hapticSelection();
    setOnboardingNotice(
      "You can enable notifications later from Profile after onboarding is complete."
    );
  }

  async function handleBiometricToggle() {
    await hapticSelection();
    setOnboardingNotice(
      "You can enable biometric lock later from Profile after confirming your password."
    );
  }

  async function finishOnboarding() {
    setSaving(true);
    setOnboardingNotice("");

    setLocalOnboardingDone(user.uid);

    try {
      const userRef = doc(db, "users", user.uid);

      await setDoc(
        userRef,
        {
          onboardingCompleted: true,
          onboardingCompletedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn("Parent onboarding completion could not be saved, continuing:", err);
    } finally {
      setCompleted(true);
      await hapticSuccess();
      setSaving(false);

      window.setTimeout(() => {
        navigate("/parent/home", { replace: true });
      }, 50);
    }
  }

  return (
    <div className="pp-onboarding-screen">
      <div className="pp-onboarding-shell">
        <div className="pp-onboarding-progress">
          {STEPS.map((item, idx) => (
            <span
              key={item}
              className={`pp-onboarding-dot ${idx <= stepIndex ? "active" : ""}`}
            />
          ))}
        </div>

        <div className="pp-onboarding-card">
          {currentStep === "welcome" ? (
            <StepWelcome
              parentName={effectiveProfile?.displayName || effectiveProfile?.name || user?.displayName || "Parent"}
            />
          ) : null}

          {currentStep === "child" ? (
            <StepChild
              children={resolvedChildren}
              schoolName={resolvedSchoolName}
            />
          ) : null}

          {currentStep === "notifications" ? (
            <StepNotifications />
          ) : null}

          {currentStep === "biometric" ? (
            <StepBiometric />
          ) : null}

          {onboardingNotice ? (
            <div className="pp-onboarding-notice" role="status">
              {onboardingNotice}
            </div>
          ) : null}

          <div className="pp-onboarding-actions">
            {stepIndex > 0 ? (
              <button
                type="button"
                className="pp-onboarding-btn secondary"
                onClick={prevStep}
                disabled={saving}
              >
                Back
              </button>
            ) : (
              <span />
            )}

            <button
              type="button"
              className="pp-onboarding-btn primary"
              onClick={nextStep}
              disabled={saving}
            >
              {stepIndex === STEPS.length - 1
                ? saving
                  ? "Finishing..."
                  : "Go to dashboard"
                : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepWelcome({ parentName }) {
  const firstName = String(parentName || "Parent").split(" ")[0];

  return (
    <div className="pp-onboarding-step">
      <div className="pp-onboarding-icon">👋</div>
      <p className="pp-onboarding-eyebrow">Welcome to PresentPlus</p>
      <h1 className="pp-onboarding-title">Hello, {firstName}</h1>
      <p className="pp-onboarding-copy">
        Track attendance, receive school updates, and stay connected in one calm
        parent space.
      </p>

      <div className="pp-onboarding-feature-list">
        <div className="pp-onboarding-feature-pill">Attendance visibility</div>
        <div className="pp-onboarding-feature-pill">School updates</div>
        <div className="pp-onboarding-feature-pill">Fast absence reporting</div>
      </div>
    </div>
  );
}

function StepChild({ children, schoolName }) {
  const linkedChildren = Array.isArray(children) ? children : [];

  return (
    <div className="pp-onboarding-step">
      <div className="pp-onboarding-icon">🎒</div>
      <p className="pp-onboarding-eyebrow">Check your linked children</p>
      <h1 className="pp-onboarding-title">Everything looks ready</h1>
      <p className="pp-onboarding-copy">
        This account is connected to your school and linked child profiles.
      </p>

      <div className="pp-onboarding-summary">
        {linkedChildren.length ? (
          linkedChildren.map((child, index) => (
            <div
              key={child?.id || `${child?.name || "child"}-${index}`}
              className="pp-onboarding-summary-row"
            >
              <span>{linkedChildren.length > 1 ? `Child ${index + 1}` : "Child"}</span>
              <strong>
                {child?.name || "Child"}
                {child?.year ? ` · ${child.year}` : ""}
              </strong>
            </div>
          ))
        ) : (
          <div className="pp-onboarding-summary-row">
            <span>Child</span>
            <strong>Child not assigned</strong>
          </div>
        )}

        <div className="pp-onboarding-summary-row">
          <span>School</span>
          <strong>{schoolName}</strong>
        </div>

        <div className="pp-onboarding-summary-row">
          <span>Linked children</span>
          <strong>{linkedChildren.length}</strong>
        </div>
      </div>
    </div>
  );
}

function StepNotifications() {
  return (
    <div className="pp-onboarding-step">
      <div className="pp-onboarding-icon">🔔</div>
      <p className="pp-onboarding-eyebrow">Stay informed</p>
      <h1 className="pp-onboarding-title">Notifications</h1>
      <p className="pp-onboarding-copy">
        PresentPlus can send alerts for attendance, school notices, and important
        parent updates.
      </p>

      <div
        className="pp-onboarding-toggle-card"
        style={{
          width: "calc(100% - 20px)",
          maxWidth: "calc(100% - 20px)",
          margin: "0 auto",
          boxSizing: "border-box",
          padding: "18px 20px",
        }}
      >
        <div>
          <strong>Set up later from Profile</strong>
          <small>
            After onboarding, open Profile to choose whether notifications are enabled.
          </small>
        </div>
      </div>
    </div>
  );
}

function StepBiometric() {
  return (
    <div className="pp-onboarding-step">
      <div className="pp-onboarding-icon">🔐</div>
      <p className="pp-onboarding-eyebrow">Extra security</p>
      <h1 className="pp-onboarding-title">Biometric lock</h1>
      <p className="pp-onboarding-copy">
        Fingerprint, face unlock, or device security can protect the parent app
        after normal sign-in.
      </p>

      <div
        className="pp-onboarding-toggle-card"
        style={{
          width: "calc(100% - 20px)",
          maxWidth: "calc(100% - 20px)",
          margin: "0 auto",
          boxSizing: "border-box",
          padding: "18px 20px",
        }}
      >
        <div>
          <strong>Set up later from Profile</strong>
          <small>
            After onboarding, open Profile and confirm your password to enable Quick Unlock.
          </small>
        </div>
      </div>
    </div>
  );
}
